<script setup lang="ts">
const config = useRuntimeConfig();
const cc = config.public.clientConfig as any;
const appName = cc?.appName ?? 'SpreadIt';
const landing = cc?.landing ?? {};
const tagline = landing.tagline ?? 'Supercharge your spreadsheets with Code, APIs, and AI.';
const subtitle = landing.subtitle ?? 'Connect Google Sheets, attach processing templates, and run enrichments — no infrastructure required.';

const { status } = useAuth();
watchEffect(() => {
  if (status.value === 'authenticated') navigateTo('/dashboard', { replace: true });
});

const features = [
  { icon: 'i-heroicons-table-cells',        title: 'Google Sheets Native',            body: 'Sync any spreadsheet in seconds. SpreadIt reads your data live and writes computed columns back automatically.',   color: 'text-green-500'  },
  { icon: 'i-heroicons-code-bracket-square', title: 'JavaScript Processing Templates', body: 'Write sandboxed async functions that run per-row. Call APIs, enrich data, classify records — anything JS can do.', color: 'text-blue-500'   },
  { icon: 'i-heroicons-arrows-right-left',   title: 'Smart Field Mapping',             body: 'Alias column names or inject constant values before they reach your template. No code changes when sheets evolve.',  color: 'text-purple-500' },
  { icon: 'i-heroicons-key',                 title: 'Encrypted Credentials',           body: 'Store API keys encrypted at rest with AES-256-GCM. Templates access them — your browser never sees the values.',    color: 'text-amber-500'  },
  { icon: 'i-heroicons-queue-list',          title: 'Library Functions',               body: 'Publish reusable helpers to your org library or the public catalog. Attach them to any template in one click.',      color: 'text-pink-500'   },
  { icon: 'i-heroicons-clock',               title: 'Scheduled Runs',                  body: 'Set cron schedules per tab. SpreadIt syncs, processes, and writes back — hands-free, every 15 min to weekly.',      color: 'text-teal-500'   },
];
</script>

<template>
  <!-- Hero -->
  <section class="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-950 dark:via-gray-950 dark:to-primary-950 text-gray-900 dark:text-white">
    <div class="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-primary-400/10 dark:bg-primary-600/10 blur-[120px] pointer-events-none" />
    <div class="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-indigo-400/10 dark:bg-blue-600/10 blur-[100px] pointer-events-none" />

    <div class="relative max-w-5xl mx-auto px-6 py-28 text-center">
      <div class="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium bg-primary-100 dark:bg-primary-500/10 border border-primary-300 dark:border-primary-500/30 text-primary-700 dark:text-primary-400 mb-8">
        <UIcon name="i-heroicons-sparkles" />
        Spreadsheet-powered automation, reimagined
      </div>
      <h1 class="text-5xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6 bg-gradient-to-r from-gray-900 via-primary-700 to-indigo-600 dark:from-white dark:via-gray-100 dark:to-gray-400 bg-clip-text text-transparent">
        {{ tagline }}
      </h1>
      <p class="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
        {{ subtitle }}</p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <UButton size="xl" color="primary" to="/auth/signup" icon="i-heroicons-arrow-right" trailing>Start for free</UButton>
        <UButton size="xl" variant="outline" color="neutral" to="/auth/signin">Sign in</UButton>
      </div>
    </div>
  </section>

  <!-- How it works -->
  <section class="max-w-5xl mx-auto px-6 py-16">
    <h2 class="text-2xl font-bold text-center mb-8 text-gray-800 dark:text-gray-100">How it works</h2>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
      <div v-for="(step, i) in [
        { n: '1', title: 'Connect your Sheet',   body: 'Paste a Google Sheets ID and share it with the SpreadIt service account. Data is synced in seconds.' },
        { n: '2', title: 'Attach a Template',    body: 'Write or pick a JavaScript template that processes each row. Use credentials, APIs, or library functions.' },
        { n: '3', title: 'Run & Write Back',     body: 'Trigger manually, via API, or on a cron schedule. Results are written back to your sheet automatically.' },
      ]" :key="i" class="rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 p-6 space-y-3">
        <span class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary-500/15 text-primary-600 dark:text-primary-400 font-bold text-lg">{{ step.n }}</span>
        <h3 class="text-base font-semibold text-gray-800 dark:text-white">{{ step.title }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ step.body }}</p>
      </div>
    </div>
  </section>

  <!-- Features grid -->
  <section class="max-w-5xl mx-auto px-6 pb-16">
    <h2 class="text-2xl font-bold text-center mb-8 text-gray-800 dark:text-gray-100">Everything you need</h2>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      <div
        v-for="f in features"
        :key="f.title"
        class="rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 p-5 space-y-3 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
      >
        <UIcon :name="f.icon" :class="['text-2xl', f.color]" />
        <h3 class="font-semibold text-gray-800 dark:text-white">{{ f.title }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ f.body }}</p>
      </div>
    </div>
  </section>

  <!-- Use Cases Showcase -->
  <section class="max-w-5xl mx-auto px-6 pb-16">
    <div class="text-center mb-10">
      <h2 class="text-3xl font-extrabold mb-3 text-gray-900 dark:text-white">Built for Everyone</h2>
      <p class="text-gray-500 max-w-xl mx-auto text-lg">
        Explore simple automations or advanced API integrations.
      </p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 text-left">
      <div class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 shadow-sm p-6 hover:border-blue-300 dark:hover:border-blue-700 transition">
        <UIcon name="i-heroicons-bolt" class="text-4xl text-blue-500 mb-4" />
        <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">Workflow Automation</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Streamline repetitive tasks by executing custom Javascript functions securely over your dataset. For Project Managers, HR professionals, and more.</p>
      </div>
      <div class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 shadow-sm p-6 hover:border-stone-300 dark:hover:border-stone-700 transition">
        <UIcon name="i-heroicons-circle-stack" class="text-4xl text-stone-500 mb-4" />
        <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">Data Enrichment</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Connect native LLMs and custom APIs to automatically extract, format, and populate your columns. For Teachers, Legal, Marketers, and more.</p>
      </div>
      <div class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 shadow-sm p-6 hover:border-emerald-300 dark:hover:border-emerald-700 transition">
        <UIcon name="i-heroicons-arrows-right-left" class="text-4xl text-emerald-500 mb-4" />
        <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">API Integrations</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Push and pull data directly from CRMs and external services without building standalone integrations. For Developers, Data Analysts, and more.</p>
      </div>
    </div>

    <div class="text-center">
      <UButton size="xl" color="primary" variant="subtle" to="/use-cases" icon="i-heroicons-arrow-right" trailing>
        Explore Use Cases
      </UButton>
    </div>
  </section>

  <!-- CTA banner -->
  <section class="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40">
    <div class="max-w-6xl mx-auto px-6 py-16 text-center space-y-5">
      <h2 class="text-2xl font-bold text-gray-800 dark:text-white">Ready to automate your data workflows?</h2>
      <p class="text-gray-500 dark:text-gray-400">Get started in minutes. No credit card required.</p>
      <UButton size="xl" color="primary" to="/auth/signup">Create your free account</UButton>
    </div>
  </section>
</template>
